import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { analytics } from 'analytics';
import Button from 'ui/Button';
import Dialog from 'ui/Dialog';
import Icon from 'ui/Icon';
import { toaster } from 'ui/Toaster';

import { useTemplateSankeysWithSVGQuery } from '../../api';
import { useApiContext } from '../../contexts/apiContext';
import { useAppPlatform } from '../../utils/app';
import { pathToSankey } from '../../utils/routes';
import { buildTemplateCopy } from '../../utils/sankey';
import { useCurrentUser } from '../../utils/user';
import TemplateDiagramsView from '../TemplateDiagramsView';

type Props = {
  open: boolean;
  onClose: () => void;
};

// TODO: Rename to NewDiagramFromTemplateDialog and integrate with opener trigger
const NewDiagramFromTemplateModal: React.FC<Props> = ({ open, onClose }) => {
  const { api } = useApiContext();
  const { user, refetchUser } = useCurrentUser();
  const createdWith = useAppPlatform();
  const navigate = useNavigate();

  const [selectedDiagramId, setSelectedDiagramId] = useState<number>();

  // Fetch all templates
  const query = useTemplateSankeysWithSVGQuery(open);
  const templateSankeys = useMemo(() => query.data ?? [], [query.data]);
  const selectedTemplate = useMemo(
    () => templateSankeys.find((template) => template.id === selectedDiagramId),
    [templateSankeys, selectedDiagramId]
  );

  const { mutate: copyTemplateSankey, isLoading: isCopying } = useMutation({
    mutationFn: async () => {
      if (selectedTemplate) {
        try {
          // Create new Sankey diagram and refetch user to make sure permissions are up to date
          const { data } = await api().sankeys.create({
            sankeyRequest: buildTemplateCopy(
              selectedTemplate,
              user,
              createdWith
            ),
          });
          await refetchUser();
          toaster.success({ title: 'New diagram created successfully' });
          onClose();
          navigate(pathToSankey(data.id));
        } catch (error) {
          analytics.reportError('Error creating new diagram from template', {
            selectedTemplate,
            error,
          });
        }
      }
    },
  });

  // Reset selected diagram when modal is closed
  useEffect(() => {
    if (!open) {
      setSelectedDiagramId(undefined);
    }
  }, [open]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    copyTemplateSankey();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onClose} size="cover">
      <Dialog.Content>
        <form onSubmit={handleSubmit}>
          <Dialog.Header>
            <Icon fontSize="md" icon="file-regular" mr={2} /> Choose a template
          </Dialog.Header>
          <Dialog.Body>
            <TemplateDiagramsView
              isLoading={query.isLoading}
              templateSankeys={templateSankeys}
              selectedDiagramId={selectedDiagramId}
              onClick={(id) => setSelectedDiagramId(id)}
              onDoubleClick={() => copyTemplateSankey()}
            />
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              type="submit"
              variant="primary-solid"
              disabled={!selectedDiagramId}
              loading={isCopying}
              title={
                !selectedDiagramId
                  ? 'Please select a template for the new diagram'
                  : undefined
              }
            >
              Create a new diagram
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default NewDiagramFromTemplateModal;
